import { NgComponentOutlet } from "@angular/common";
import { Component, inject, input } from "@angular/core";
import { AssistantMessage, Message, ToolCall, ToolMessage } from "@ag-ui/client";
import { AgentKitService } from "./agent-kit.service";
import { FrontendToolConfig, AngularToolCall, RenderToolCallConfig } from "./tools";
import { partialJSONParse } from "@copilotkit/shared";

type ToolCallHandler =
  | { type: "renderer"; config: RenderToolCallConfig }
  | { type: "clientTool"; config: FrontendToolConfig };

@Component({
  selector: "agent-render-tool-calls",
  standalone: true,
  imports: [NgComponentOutlet],
  template: `
    @for (toolCall of message().toolCalls ?? []; track toolCall.id) { @let renderConfig =
    pickRenderer(toolCall.function.name); @if (renderConfig?.config.component) {
    <ng-container *ngComponentOutlet="renderConfig.config.component; inputs: { toolCall: buildToolCall(toolCall) }" />
    } }
  `,
})
export class RenderToolCalls {
  readonly #agentKit = inject(AgentKitService);

  readonly message = input.required<AssistantMessage>();

  readonly messages = input.required<Message[]>();

  readonly isLoading = input<boolean>(false);

  protected pickRenderer(name: string): ToolCallHandler | undefined {
    const messageAgentId = (this.message() as AssistantMessage & { agentId?: string }).agentId;

    const renderer = this.#agentKit
      .toolCallRenderConfigs()
      .find((c) => c.name === name && (c.agentId === undefined || c.agentId === messageAgentId));
    if (renderer) return { type: "renderer", config: renderer };

    const clientTool = this.#agentKit
      .clientToolCallRenderConfigs()
      .find((c) => c.name === name && (c.agentId === undefined || c.agentId === messageAgentId));
    if (clientTool) return { type: "clientTool", config: clientTool };

    const starRenderer = this.#agentKit.toolCallRenderConfigs().find((c) => c.name === "*");
    if (starRenderer) return { type: "renderer", config: starRenderer };

    return undefined;
  }

  protected buildToolCall<Args extends Record<string, unknown>>(toolCall: ToolCall): AngularToolCall<Args> {
    const args = partialJSONParse(toolCall.function.arguments) as Args;
    const message = this.#getToolMessage(toolCall.id);

    if (message) {
      return { args, status: "complete", result: message.content };
    } else if (this.isLoading()) {
      return { args, status: "in-progress", result: undefined };
    } else {
      return { args, status: "executing", result: undefined };
    }
  }

  #getToolMessage(toolCallId: string): ToolMessage | undefined {
    return this.messages().find((m): m is ToolMessage => m.role === "tool" && m.toolCallId === toolCallId);
  }
}
